import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { clipOfClient, clipQueryOptions, clipTimelineQueryOptions } from '~/entities/exercise-clip';
import {
  exercisesByIdsQueryOptions,
  stillUrl,
  toExerciseListItems,
  useExerciseQuery,
  useOtherLanguageNamesOf,
  type ExerciseCatalogueView,
  type ExerciseQueryState,
} from '~/entities/exercise-library';
import { useLiftHistory, type LiftLine } from '~/entities/lift-history';
import { useSessionUser } from '~/entities/session';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { preferenceStore } from '~/shared/lib/preferenceStore';

import {
  clientTrainedExercisesQueryKey,
  clientTrainedExercisesQueryOptions,
} from '../api/clientDetailQueries';
import { toTrainedExercises } from '../api/clientDetailSource';
import {
  buildExerciseList,
  narrowExerciseList,
  readWithClipOnly,
  withClipAfterClearing,
  writeWithClipOnly,
  type FormaExerciseRow,
  type FormaNarrowedList,
} from '../model/formaExercises';
import {
  buildForma,
  defaultFormaSelection,
  moveFormaSelection,
  type FormaView,
} from '../model/formaView';
import type { SelectionMove, TwoDatesSelection } from '../model/twoDates';

import { useFormaDockPanel, type FormaDockPanel } from './useFormaDockPanel';

export interface FormaOptions {
  readonly line: LiftLine;
  readonly trend: boolean;
  readonly projection: boolean;
  /** The clip the player is showing, when the tab was opened through one. */
  readonly openClipId: number | null;
}

export interface FormaState {
  readonly list: {
    readonly pending: boolean;
    readonly failed: boolean;
    /** The rows the list shows, narrowed by the search, the facets and „Z nagraniem". */
    readonly rows: readonly FormaExerciseRow[];
    /** Every movement, whatever the list shows. */
    readonly all: readonly FormaExerciseRow[];
    readonly withClipOnly: boolean;
    readonly toggleWithClipOnly: () => void;
    readonly retry: () => void;
    /** The library's search and facets over this client's movements. Per visit, as the library's are. */
    readonly search: ExerciseQueryState & {
      readonly catalogue: ExerciseCatalogueView;
      /** What narrows the list, which decides what an empty one says. */
      readonly narrowedBy: FormaNarrowedList['narrowedBy'];
      /** „Wyczyść filtry": the search and the facets off, and „Z nagraniem" when it alone emptied the list. */
      readonly clearAll: () => void;
    };
  };
  readonly exercise: FormaExerciseRow | null;
  readonly choose: (exerciseId: number) => void;
  /** The exercise's history AND its recordings — one section, one wait. */
  readonly pending: boolean;
  readonly failed: boolean;
  readonly retry: () => void;
  readonly view: FormaView;
  readonly move: (move: SelectionMove) => void;
  /** A recorded point's recording into the panel. */
  readonly showRecorded: (clipId: number) => void;
  readonly panel: FormaDockPanel;
}

/**
 * Whether the tab strip's „Forma" carries its dot: any movement with a recording or a comment from
 * the client that the trainer has not seen (R9). The same cache entry the tab's own list reads.
 */
export function useFormaUnread(clientId: number): boolean {
  const lang = useContentLanguage();
  const trained = useQuery(clientTrainedExercisesQueryOptions(clientId, lang));
  return toTrainedExercises(trained.data).some((exercise) => exercise.unread);
}

/** The trainer saw or answered a clip: the movement list's unread marks are re-read. */
export function useRefreshTrainedExercises(clientId: number): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: clientTrainedExercisesQueryKey(clientId) });
  };
}

interface HeldSelection {
  readonly exerciseId: number;
  readonly value: TwoDatesSelection;
}

export function useForma(clientId: number, options: FormaOptions): FormaState {
  const { t, i18n } = useTranslation();
  const lang = useContentLanguage();
  const viewer = useSessionUser();
  const [now] = useState(Date.now);

  const trained = useQuery(clientTrainedExercisesQueryOptions(clientId, lang));
  const sources = trained.isSuccess ? toTrainedExercises(trained.data) : [];
  const covers = useQuery(
    exercisesByIdsQueryOptions(
      sources.map((source) => source.exerciseId),
      lang,
    ),
  );
  const described = covers.isSuccess ? toExerciseListItems(covers.data) : [];
  const otherNames = useOtherLanguageNamesOf(sources.map((source) => source.exerciseId));
  const coverMap = new Map(described.map((item) => [item.id, stillUrl(item)]));
  const rows = buildExerciseList(sources, coverMap, { t, locale: i18n.language });
  const [withClipOnly, setWithClipOnly] = useState(() =>
    readWithClipOnly(preferenceStore(), viewer?.id ?? null),
  );
  const exerciseQuery = useExerciseQuery();
  const narrowed = narrowExerciseList(
    rows,
    described,
    { query: exerciseQuery.query, withClipOnly },
    i18n.language,
    otherNames.names,
  );
  const writeClipOnly = (on: boolean) => {
    setWithClipOnly(on);
    writeWithClipOnly(preferenceStore(), viewer?.id ?? null, on);
  };

  const [chosen, setChosen] = useState<number | null>(null);
  const openClip = useQuery({
    ...clipQueryOptions(options.openClipId ?? 0),
    enabled: options.openClipId !== null,
  });
  // `?clip=` is editable, so a clip of another client never chooses the movement.
  const openedExercise = clipOfClient(openClip.data, clientId)?.exerciseId ?? null;
  if (chosen === null && openedExercise !== null) setChosen(openedExercise);

  const exerciseId = chosen ?? rows[0]?.exerciseId ?? null;
  const exercise = rows.find((row) => row.exerciseId === exerciseId) ?? null;

  const history = useLiftHistory(clientId, exerciseId);
  // The catalogue already says what the movement measures, so its lines are known before its sessions.
  const listedFields = described.find((item) => item.id === exerciseId)?.measurementFields ?? null;
  const clips = useQuery({
    ...clipTimelineQueryOptions(clientId, exerciseId ?? 0),
    enabled: exerciseId !== null,
  });
  const panel = useFormaDockPanel(exerciseId, clips.data);

  const [held, setHeld] = useState<HeldSelection | null>(null);
  const selection =
    held !== null && held.exerciseId === exerciseId
      ? held.value
      : defaultFormaSelection(history.sessions);


  return {
    list: {
      pending: trained.isPending || (trained.isError && trained.isFetching),
      failed: trained.isError,
      rows: narrowed.rows,
      all: rows,
      withClipOnly,
      toggleWithClipOnly: () => {
        writeClipOnly(!withClipOnly);
      },
      retry: () => {
        void trained.refetch();
      },
      search: {
        ...exerciseQuery,
        catalogue: narrowed.catalogue,
        narrowedBy: narrowed.narrowedBy,
        clearAll: () => {
          exerciseQuery.reset();
          setWithClipOnly(withClipAfterClearing(withClipOnly, narrowed.narrowedBy));
        },
      },
    },
    exercise,
    choose: setChosen,
    pending: history.pending || clips.isPending || (clips.isError && clips.isFetching),
    failed: history.failed || clips.isError,
    retry: () => {
      history.retry();
      void clips.refetch();
    },
    view: buildForma(
      {
        sessions: history.sessions,
        clips: clips.data ?? [],
        fields: history.fields ?? listedFields,
        line: options.line,
        selection,
        trend: options.trend,
        projection: options.projection,
        viewerUserId: viewer?.id ?? null,
        now,
        dockClipId: panel.chosenClipId,
      },
      { t, locale: i18n.language },
    ),
    move: (move) => {
      if (exerciseId === null) return;
      const next = moveFormaSelection(history.sessions, selection, move);
      if (next !== null) setHeld({ exerciseId, value: next });
    },
    showRecorded: panel.showInPanel,
    panel,
  };
}
